import { apiFetch } from "../api/client";
import { useTenantScope, normalizeScopePlatform } from "../scope/TenantScopeContext";
import React, { useCallback, useEffect, useMemo, useState } from "react";

const API = process.env.REACT_APP_API_URL || "https://api.wirelesspress.org";

function storedProfile() {
  for (const key of ["user", "currentUser", "profile"]) {
    try {
      const value = JSON.parse(localStorage.getItem(key) || "null");
      if (value && typeof value === "object") return value;
    } catch (_) {}
  }
  return {};
}

function authHeaders(json = false) {
  const token =
    localStorage.getItem("access_token") ||
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    "";
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(json ? { "Content-Type": "application/json" } : {}),
  };
}

async function apiJson(path, options = {}) {
  const response = await apiFetch(`${path}`, options);
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(body.detail || `Request failed with HTTP ${response.status}`);
  }
  return body;
}

const panel = {
  background: "#101923",
  border: "1px solid #263747",
  borderRadius: 14,
  padding: 20,
  marginBottom: 18,
};

const button = {
  border: 0,
  borderRadius: 8,
  padding: "9px 13px",
  color: "white",
  background: "#285d7a",
  cursor: "pointer",
  fontWeight: 700,
};

const input = {
  width: "100%",
  boxSizing: "border-box",
  background: "#0a1118",
  color: "#eef5fb",
  border: "1px solid #31485b",
  borderRadius: 8,
  padding: "10px 12px",
};

const colors = {
  online: "#39d98a",
  offline: "#f5a524",
  stale: "#ff5c5c",
  unknown: "#a9bbca",
  revoked: "#d97843",
  decommission_pending: "#b79645",
  decommissioned: "#8b98a5",
};

function ageLabel(seconds) {
  if (seconds == null) return "No heartbeat";
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  return `${Math.floor(seconds / 86400)}d`;
}

export default function DeviceHealthPage() {
  const scope = useTenantScope();
  const profile = useMemo(storedProfile, []);
  const role = profile.role || localStorage.getItem("role") || "";
  const companyId = profile.company_id || localStorage.getItem("company_id") || "";
  const isAdmin = ["super_admin", "company_admin"].includes(role);
  const query = role === "super_admin" && companyId ? `?company_id=${companyId}` : "";
  const settingsAvailable = role !== "super_admin" || Boolean(companyId);
  const [summary, setSummary] = useState({ status_counts: {} });
  const [devices, setDevices] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [offlineAfter, setOfflineAfter] = useState(300);
  const [staleAfter, setStaleAfter] = useState(86400);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    setError("");
    try {
      const requests = [
        apiJson(`/device-status/summary${query}`, { headers: authHeaders() }),
        apiJson(`/device-status/devices${query}`, { headers: authHeaders() }),
        apiJson(`/device-status/alerts${query ? `${query}&` : "?"}open_only=false&limit=200`, {
          headers: authHeaders(),
        }),
      ];
      if (settingsAvailable) {
        requests.push(
          apiJson(`/device-status/settings${query}`, { headers: authHeaders() })
        );
      }
      const [summaryData, deviceData, alertData, settingsData] = await Promise.all(requests);
      setSummary(summaryData || { status_counts: {} });

      let scopedDevices = Array.isArray(deviceData) ? deviceData : [];
      let scopedAlerts = Array.isArray(alertData) ? alertData : [];

      if (scope.platform && scope.platform !== "all") {
        scopedDevices = scopedDevices.filter(
          (device) =>
            normalizeScopePlatform(device.platform) === scope.platform
        );
      }

      if (scope.deviceUuid) {
        scopedDevices = scopedDevices.filter(
          (device) =>
            String(device.device_uuid) === String(scope.deviceUuid)
        );

        scopedAlerts = scopedAlerts.filter(
          (alert) =>
            String(alert.device_uuid) === String(scope.deviceUuid)
        );
      }

      setDevices(scopedDevices);
      setAlerts(scopedAlerts);
      if (settingsData) {
        setOfflineAfter(settingsData.offline_after_seconds);
        setStaleAfter(settingsData.stale_after_seconds);
      }
    } catch (err) {
      setError(err.message);
    }
  }, [query, settingsAvailable, scope.deviceUuid, scope.platform]);

  useEffect(() => {
    load();
    const timer = window.setInterval(load, 60000);
    return () => window.clearInterval(timer);
  }, [load]);

  async function post(path, payload) {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const body = {
        ...payload,
        ...(role === "super_admin" && companyId
          ? { company_id: Number(companyId) }
          : {}),
      };
      const result = await apiJson(path, {
        method: "POST",
        headers: authHeaders(true),
        body: JSON.stringify(body),
      });
      await load();
      return result;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setBusy(false);
    }
  }

  async function reconcile() {
    const result = await post("/device-status/reconcile", {});
    if (result) {
      setNotice(
        `Evaluated ${result.evaluated} devices; opened ${result.alerts_opened} and resolved ${result.alerts_resolved} alerts.`
      );
    }
  }

  async function saveSettings(event) {
    event.preventDefault();
    const result = await post("/device-status/settings", {
      offline_after_seconds: Number(offlineAfter),
      stale_after_seconds: Number(staleAfter),
    });
    if (result) setNotice("Device status thresholds saved and reconciled.");
  }

  const cards = [
    ["Online", summary.online || 0, "#39d98a"],
    ["Offline", summary.offline || 0, "#f5a524"],
    ["Stale", summary.stale || 0, "#ff5c5c"],
    ["Open alerts", summary.open_alerts || 0, "#f48fb1"],
    ["Lifecycle controlled", summary.non_active || 0, "#8b98a5"],
  ];

  return (
    <main style={{ color: "#eef5fb", padding: 24, maxWidth: 1320, margin: "0 auto" }}>
      <h1 style={{ marginBottom: 6 }}>Device Health</h1>
      <p style={{ color: "#a9bbca", marginTop: 0 }}>
        Authoritative status is calculated from server time, heartbeat freshness, tenant thresholds,
        and administrative lifecycle state. Endpoint self-reported status is not trusted.
      </p>

      {error && <div style={{ ...panel, borderColor: "#b42318", color: "#ffb4ab" }}>{error}</div>}
      {notice && <div style={{ ...panel, borderColor: "#16815d", color: "#9ce4c8" }}>{notice}</div>}

      <section style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 12, marginBottom: 18 }}>
        {cards.map(([label, value, color]) => (
          <div key={label} style={{ ...panel, marginBottom: 0 }}>
            <div style={{ color: "#a9bbca" }}>{label}</div>
            <strong style={{ color, fontSize: 30 }}>{value}</strong>
          </div>
        ))}
      </section>

      <section style={panel}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
          <div>
            <h2 style={{ margin: 0 }}>Reconciliation</h2>
            <div style={{ color: "#a9bbca" }}>
              Automatic monitor: every 60 seconds. Cache inconsistencies: {summary.status_cache_inconsistencies || 0}.
            </div>
          </div>
          {isAdmin && (
            <button style={button} disabled={busy} onClick={reconcile}>
              Reconcile now
            </button>
          )}
        </div>
      </section>

      {isAdmin && settingsAvailable && (
        <section style={panel}>
          <h2>Tenant alert thresholds</h2>
          <form onSubmit={saveSettings} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, alignItems: "end" }}>
            <label>
              Offline after seconds
              <input type="number" min="60" max="3600" value={offlineAfter} onChange={(event) => setOfflineAfter(event.target.value)} style={input} />
            </label>
            <label>
              Stale after seconds
              <input type="number" min="3600" max="2592000" value={staleAfter} onChange={(event) => setStaleAfter(event.target.value)} style={input} />
            </label>
            <button style={{ ...button, background: "#16815d" }} disabled={busy}>Save thresholds</button>
          </form>
        </section>
      )}

      <section style={panel}>
        <h2>Authoritative device status</h2>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>{["Device", "Status", "Reason", "Heartbeat age", "Lifecycle", "Evaluated"].map((name) => <th key={name} style={{ textAlign: "left", padding: 10, borderBottom: "1px solid #31485b" }}>{name}</th>)}</tr></thead>
            <tbody>
              {devices.map((device) => (
                <tr key={device.device_uuid}>
                  <td style={{ padding: 10 }}>{device.device_name || device.device_uuid}</td>
                  <td style={{ padding: 10, color: colors[device.effective_status] || "#eef5fb", fontWeight: 700 }}>{device.effective_status}</td>
                  <td style={{ padding: 10 }}>{device.status_reason}</td>
                  <td style={{ padding: 10 }}>{ageLabel(device.heartbeat_age_seconds)}</td>
                  <td style={{ padding: 10 }}>{device.lifecycle_state}</td>
                  <td style={{ padding: 10 }}>{device.evaluated_at}</td>
                </tr>
              ))}
              {!devices.length && <tr><td colSpan="6" style={{ padding: 18, color: "#a9bbca" }}>No device status rows.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      <section style={panel}>
        <h2>Offline and stale alert history</h2>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>{["Device", "Type", "Severity", "State", "Message", "Opened", "Resolved"].map((name) => <th key={name} style={{ textAlign: "left", padding: 10, borderBottom: "1px solid #31485b" }}>{name}</th>)}</tr></thead>
            <tbody>
              {alerts.map((alert) => (
                <tr key={alert.alert_uuid}>
                  <td style={{ padding: 10 }}>{alert.device_uuid}</td>
                  <td style={{ padding: 10 }}>{alert.alert_type}</td>
                  <td style={{ padding: 10, color: alert.severity === "critical" ? "#ff5c5c" : "#f5a524" }}>{alert.severity}</td>
                  <td style={{ padding: 10 }}>{alert.status}</td>
                  <td style={{ padding: 10 }}>{alert.message}</td>
                  <td style={{ padding: 10 }}>{alert.opened_at}</td>
                  <td style={{ padding: 10 }}>{alert.resolved_at || "—"}</td>
                </tr>
              ))}
              {!alerts.length && <tr><td colSpan="7" style={{ padding: 18, color: "#a9bbca" }}>No device status alerts.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>
    </main>
  );
}
